'use client'

import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, User } from 'lucide-react'
import ManagerForm from './ManagerForm'

interface Manager {
  id: string
  name: string
  email: string
  phone: string | null
  photoUrl: string | null
  isActive: boolean
  briefCount: number
}

import { Language } from '@/lib/brief613/translations'
import { useBrief613Translation } from '@/hooks/useBrief613Translation'
import { panel, panelInner, headingClass, mutedClass, ghostBtn, primaryBtn, primaryBtnInline, fieldClass, labelClass, errorBox, successBox } from './ui'

interface ManagersListProps {
  userId: string
  language: Language
}

export default function ManagersList({ userId, language }: ManagersListProps) {
  const t = useBrief613Translation(language)
  const [managers, setManagers] = useState<Manager[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingManager, setEditingManager] = useState<Manager | null>(null)

  useEffect(() => {
    loadManagers()
  }, [userId])

  const loadManagers = async () => {
    try {
      const response = await fetch('/api/brief613/managers', {
        headers: {
          'x-user-id': userId,
          'x-user-role': 'admin',
        },
      })

      const data = await response.json()

      if (data.success) {
        setManagers(data.managers)
      }
    } catch (error) {
      console.error('Error loading managers:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (managerId: string, managerName: string) => {
    if (!confirm(`${t.manager.deleteConfirm} ${managerName}?`)) {
      return
    }

    try {
      const response = await fetch(`/api/brief613/managers/${managerId}`, {
        method: 'DELETE',
        headers: {
          'x-user-id': userId,
          'x-user-role': 'admin',
        },
      })

      const data = await response.json()

      if (data.success) {
        loadManagers()
      } else {
        alert(data.error || t.common.error)
      }
    } catch (error) {
      console.error('Error deleting manager:', error)
      alert(t.common.error)
    }
  }

  if (showForm || editingManager) {
    return (
      <ManagerForm
        manager={editingManager}
        userId={userId}
        onSuccess={() => {
          setShowForm(false)
          setEditingManager(null)
          loadManagers()
        }}
        onCancel={() => {
          setShowForm(false)
          setEditingManager(null)
        }}
      />
    )
  }

  if (loading) {
    return (
      <div className={`${panel} p-8 text-center`}>
        <div className="w-12 h-12 border-4 border-white/20 border-t-white/80 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/55">Loading managers...</p>
      </div>
    )
  }

  return (
    <div className={`${panel} p-6`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-base font-semibold text-white/90">{t.manager.title}</h3>
        <button onClick={() => setShowForm(true)} className={primaryBtnInline}>
          <Plus size={18} className="text-white/50" />
          {t.manager.add}
        </button>
      </div>

      {managers.length === 0 ? (
        <div className="text-center py-12">
          <User className="mx-auto mb-4 text-white/30" size={32} />
          <p className="text-white/45 text-xs mb-4">No project managers yet</p>
          <button onClick={() => setShowForm(true)} className={primaryBtnInline}>
            Add Your First Manager
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {managers.map((manager) => (
            <div key={manager.id} className={`${panelInner} p-4 border-l-2 border-l-white/20`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                    {manager.photoUrl ? (
                      <img src={manager.photoUrl} alt={manager.name} className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <User size={24} className="text-white/50" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-white/90">
                      {(manager as any).pronom ? `${(manager as any).pronom} ` : ''}{manager.name}
                    </h4>
                    <p className="text-sm text-white/60">{manager.email}</p>
                    {(manager as any).username && (
                      <p className="text-xs text-white/50 mt-1">Username: {(manager as any).username}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingManager(manager)}
                    className="text-white/60 hover:text-white transition-colors"
                  >
                    <Edit size={18} className="text-white/50" />
                  </button>
                  <button
                    onClick={() => handleDelete(manager.id, manager.name)}
                    className="text-red-400/60 hover:text-red-400 transition-colors"
                    disabled={manager.briefCount > 0}
                    title={manager.briefCount > 0 ? t.manager.cannotDelete : t.manager.delete}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              {manager.phone && (
                <p className="text-sm text-white/70 mb-2">📞 {manager.phone}</p>
              )}
              <div className="flex items-center justify-between mt-4">
                <span className={`text-[10px] px-2 py-0.5 rounded-md border ${manager.isActive ? 'bg-emerald-500/10 text-emerald-300/90 border-emerald-500/20' : 'bg-white/5 text-white/50 border-white/10'}`}>
                  {manager.isActive ? 'Active' : 'Inactive'}
                </span>
                <span className="text-xs text-white/50">{manager.briefCount} {t.manager.briefCount}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

